/**
 * Unified Climate Target Types
 *
 * Single source of truth for all climate target interfaces.
 * Used by frontend, backend AI, and HVAC systems.
 */

/**
 * A range with min, optimal (setpoint), and max values.
 */
export interface TargetRange {
  min: number;
  optimal: number;
  max: number;
}

/**
 * Full climate targets for a single period (day or night).
 */
export interface ClimateTargets {
  temperature: TargetRange;
  humidity: TargetRange;
  vpd: TargetRange;
  co2: TargetRange;
}

/**
 * Day + night climate targets for a single flowering week.
 * This is the Climate Recipe unit -- one entry per week in the batch schedule.
 */
export interface ClimateRecipeWeek {
  week: number;

  day: ClimateTargets;
  night: ClimateTargets;

  lightPercent: number;
  ppfd?: number;
  lightHours: number;   // hours of light per day (e.g. 18 for veg, 12 for flower)
}

/**
 * The active resolved targets for a room at a given moment.
 * Written to rooms/{id}.climate by the batch sync listener.
 * Consumers pick day or night based on the current light schedule.
 */
export interface RoomClimateTargets {
  day: ClimateTargets;
  night: ClimateTargets;
  lightPercent?: number;
  lightHours?: number;
  updatedAt?: string;

  // Legacy flat fields kept for backward compatibility during migration.
  // New code should use day/night above. These are auto-populated from day values.
  temperature?: TargetRange;
  humidity?: TargetRange;
  vpd?: TargetRange;
  co2?: TargetRange;
}

// Default targets used when no batch is active.
export const DEFAULT_DAY_TARGETS: ClimateTargets = {
  temperature: { min: 23, optimal: 26, max: 29 },
  humidity:    { min: 55, optimal: 60, max: 70 },
  vpd:         { min: 0.9, optimal: 1.1, max: 1.4 },
  co2:         { min: 800, optimal: 1000, max: 1300 },
};

export const DEFAULT_NIGHT_TARGETS: ClimateTargets = {
  temperature: { min: 19, optimal: 22, max: 25 },
  humidity:    { min: 58, optimal: 63, max: 72 },
  vpd:         { min: 0.7, optimal: 0.9, max: 1.2 },
  co2:         { min: 400, optimal: 600, max: 800 },
};

export const DEFAULT_ROOM_CLIMATE_TARGETS: RoomClimateTargets = {
  day:   DEFAULT_DAY_TARGETS,
  night: DEFAULT_NIGHT_TARGETS,
  lightPercent: 100,
  lightHours: 12,
  // Legacy flat (day values)
  temperature: DEFAULT_DAY_TARGETS.temperature,
  humidity:    DEFAULT_DAY_TARGETS.humidity,
  vpd:         DEFAULT_DAY_TARGETS.vpd,
  co2:         DEFAULT_DAY_TARGETS.co2,
};

// Default targets for drying rooms.
export const DRYING_DAY_TARGETS: ClimateTargets = {
  temperature: { min: 18, optimal: 20, max: 21 },
  humidity:    { min: 55, optimal: 60, max: 65 },
  vpd:         { min: 0.6, optimal: 0.8, max: 1.0 },
  co2:         { min: 400, optimal: 600, max: 800 },
};

export const DRYING_NIGHT_TARGETS: ClimateTargets = {
  temperature: { min: 17, optimal: 19, max: 21 },
  humidity:    { min: 55, optimal: 60, max: 65 },
  vpd:         { min: 0.5, optimal: 0.7, max: 0.9 },
  co2:         { min: 400, optimal: 600, max: 800 },
};

// Default targets for curing rooms.
export const CURING_DAY_TARGETS: ClimateTargets = {
  temperature: { min: 18, optimal: 20, max: 22 },
  humidity:    { min: 58, optimal: 62, max: 65 },
  vpd:         { min: 0.5, optimal: 0.7, max: 0.9 },
  co2:         { min: 400, optimal: 600, max: 800 },
};

export const CURING_NIGHT_TARGETS: ClimateTargets = {
  temperature: { min: 17, optimal: 19, max: 22 },
  humidity:    { min: 58, optimal: 62, max: 65 },
  vpd:         { min: 0.4, optimal: 0.6, max: 0.8 },
  co2:         { min: 400, optimal: 600, max: 800 },
};

/**
 * Return the appropriate default day+night targets for a room based on its category and type.
 * Dry rooms (drying/curing) use lower temperature and tighter humidity than production rooms.
 */
export function getDefaultTargets(
  category?: string,
  roomType?: string,
): { day: ClimateTargets; night: ClimateTargets } {
  if (category === 'dry') {
    if (roomType === 'curing') {
      return { day: CURING_DAY_TARGETS, night: CURING_NIGHT_TARGETS };
    }
    return { day: DRYING_DAY_TARGETS, night: DRYING_NIGHT_TARGETS };
  }
  return { day: DEFAULT_DAY_TARGETS, night: DEFAULT_NIGHT_TARGETS };
}

// ---------------------------------------------------------------------------
// Single-setpoint helpers
//
// The UI exposes a single "optimal" value per metric, and we derive the
// surrounding {min, max} band symmetrically from these offsets so the AI
// scoring + warning thresholds keep matching the prior behaviour.
// ---------------------------------------------------------------------------

export const TARGET_OFFSETS = {
  temperature: 3,
  humidity: 7,
  co2: 200,
  vpd: null,
} as const;

export const TARGET_STEPS = {
  temperature: 0.5,
  humidity: 1,
  vpd: 0.05,
  co2: 50,
} as const;

export type MetricKey = 'temperature' | 'humidity' | 'vpd' | 'co2';

export function snapToStep(metric: MetricKey, value: number): number {
  const step = TARGET_STEPS[metric];
  const snapped = Math.round(value / step) * step;
  const decimals = metric === 'vpd' ? 2 : metric === 'temperature' ? 1 : 0;
  const factor = Math.pow(10, decimals);
  return Math.round(snapped * factor) / factor;
}

export function applyDerivedRange(
  prev: TargetRange | undefined,
  metric: MetricKey,
  value: number,
): TargetRange {
  const snapped = snapToStep(metric, value);
  if (metric === 'vpd') {
    const r2 = (n: number) => Math.round(n * 100) / 100;
    // VPD is a derived value: always RECENTER the band around the new optimal so
    // it can never drift off-center when temp/RH change. Preserve the operator's
    // chosen band half-width when a valid prior band exists, else default ±0.3.
    let half = 0.3;
    if (prev && typeof prev.min === 'number' && typeof prev.max === 'number' && prev.max > prev.min) {
      half = (prev.max - prev.min) / 2;
    }
    return { min: r2(snapped - half), optimal: snapped, max: r2(snapped + half) };
  }
  const off = TARGET_OFFSETS[metric];
  if (off === null || off === undefined) {
    return { min: snapped, optimal: snapped, max: snapped };
  }
  const r1 = (n: number) => Math.round(n * 10) / 10;
  return {
    min: r1(snapped - off),
    optimal: snapped,
    max: r1(snapped + off),
  };
}

/**
 * Return the currently-active targets based on light state.
 */
export function resolveActiveTargets(
  roomClimate: RoomClimateTargets,
  lightState: 'day' | 'night',
): ClimateTargets {
  return lightState === 'night' ? roomClimate.night : roomClimate.day;
}
